import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  Search, LogIn, LogOut, Users, Package, Laptop,
  ScanLine, Keyboard, ChevronLeft, ChevronRight, DoorClosed,
} from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { KpiCard } from '@/components/dashboard/KpiCard';
import { TendenciaChart } from '@/components/accesos/TendenciaChart';
import { DentroAhoraPanel } from '@/components/accesos/DentroAhoraPanel';

import {
  getMovimientos, getMetricasAccesos, getDentro, getTendencia,
} from '@/services/api/accesos.api';
import type { MovimientoAccesoItem, TipoMovimiento, MetodoValidacion } from '@/types/dominio';
import { cn } from '@/lib/utils';
import { ReportButton } from '@/components/reportes/ReportButton';

const PAGE_SIZE = 20;

type TipoFiltro = 'TODOS' | TipoMovimiento;
type MetodoFiltro = 'TODOS' | MetodoValidacion;

function TipoBadge({ tipo }: { tipo: TipoMovimiento }) {
  const isEntrada = tipo === 'ENTRADA';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1',
        isEntrada
          ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
          : 'bg-amber-50 text-amber-800 ring-amber-200',
      )}
    >
      {isEntrada ? <LogIn className="h-3 w-3" /> : <LogOut className="h-3 w-3" />}
      {isEntrada ? 'Entrada' : 'Salida'}
    </span>
  );
}

function MetodoBadge({ metodo }: { metodo: MetodoValidacion }) {
  const isQr = metodo === 'QR_SCAN';
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      {isQr ? <ScanLine className="h-3.5 w-3.5" /> : <Keyboard className="h-3.5 w-3.5" />}
      {isQr ? 'QR' : 'Manual'}
    </span>
  );
}

function EquipoCell({ mov }: { mov: MovimientoAccesoItem }) {
  if (mov.equipo_corporativo) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm">
        <Laptop className="h-3.5 w-3.5 text-primary-600" />
        <span className="font-mono text-xs">{mov.equipo_corporativo.serial}</span>
      </span>
    );
  }
  if (mov.equipo_foraneo) {
    const f = mov.equipo_foraneo;
    return (
      <span className="inline-flex items-center gap-1.5 text-sm">
        <Package className="h-3.5 w-3.5 text-blue-600" />
        <span className="text-xs">
          {[f.serial, f.marca].filter(Boolean).join(' · ') || 'Foráneo'}
        </span>
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground">—</span>;
}

export function AccesosPage() {
  const [busqueda, setBusqueda] = useState('');
  const [tipo, setTipo] = useState<TipoFiltro>('TODOS');
  const [metodo, setMetodo] = useState<MetodoFiltro>('TODOS');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [page, setPage] = useState(1);

  // Cualquier cambio de filtro vuelve a la página 1.
  function resetPage<T>(setter: (v: T) => void) {
    return (v: T) => { setter(v); setPage(1); };
  }

  const filtros = {
    busqueda: busqueda.trim() || undefined,
    tipo: tipo === 'TODOS' ? undefined : tipo,
    metodo: metodo === 'TODOS' ? undefined : metodo,
    desde: desde ? `${desde}T00:00:00` : undefined,
    hasta: hasta ? `${hasta}T23:59:59` : undefined,
  };

  const { data: metricas, isLoading: loadingMetricas } = useQuery({
    queryKey: ['accesos-metricas'],
    queryFn: getMetricasAccesos,
    refetchInterval: 60_000,
  });

  const { data: dentro, isLoading: loadingDentro } = useQuery({
    queryKey: ['accesos-dentro'],
    queryFn: getDentro,
    refetchInterval: 60_000,
  });

  const { data: tendencia, isLoading: loadingTendencia } = useQuery({
    queryKey: ['accesos-tendencia'],
    queryFn: () => getTendencia(7),
    refetchInterval: 60_000,
  });

  const { data: movimientos, isLoading: loadingMovs } = useQuery({
    queryKey: ['accesos-movimientos', filtros, page],
    queryFn: () => getMovimientos({ ...filtros, page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  const totalPaginas = movimientos ? Math.max(1, Math.ceil(movimientos.total / PAGE_SIZE)) : 1;

  return (
    <div className="space-y-6">
      {/* ── Encabezado ──────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-widest text-primary-600">Control de accesos</p>
          <h2 className="text-2xl font-bold tracking-tight text-foreground">Accesos</h2>
          <p className="text-sm text-muted-foreground">
            Historial de entradas y salidas, equipos y métricas en tiempo real.
          </p>
        </div>
        <ReportButton
          reporte="accesos-movimientos"
          filtros={{
            busqueda: busqueda || undefined,
            tipo: tipo === 'TODOS' ? undefined : tipo,
            metodo: metodo === 'TODOS' ? undefined : [metodo],
            desde: desde || undefined,
            hasta: hasta || undefined,
          }}
        />
      </div>

      {/* ── KPIs ────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Ingresos hoy" value={metricas?.ingresos_hoy ?? 0} icon={LogIn} tone="success" loading={loadingMetricas} />
        <KpiCard label="Salidas hoy" value={metricas?.salidas_hoy ?? 0} icon={LogOut} tone="warning" loading={loadingMetricas} />
        <KpiCard label="Dentro ahora" value={metricas?.dentro_ahora ?? 0} icon={Users} tone="primary" loading={loadingMetricas} />
        <KpiCard label="Foráneos activos" value={metricas?.foraneos_activos ?? 0} icon={Package} tone="neutral" loading={loadingMetricas} />
      </div>

      {/* ── Tendencia + Dentro ahora ────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <TendenciaChart data={tendencia ?? []} loading={loadingTendencia} />
        </div>
        <DentroAhoraPanel data={dentro ?? []} loading={loadingDentro} />
      </div>

      {/* ── Filtros ─────────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-6">
            <div className="relative md:col-span-2 lg:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar colaborador o documento..."
                value={busqueda}
                onChange={(e) => resetPage(setBusqueda)(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={tipo} onValueChange={(v) => resetPage(setTipo)(v as TipoFiltro)}>
              <SelectTrigger><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="TODOS">Todos los tipos</SelectItem>
                <SelectItem value="ENTRADA">Entradas</SelectItem>
                <SelectItem value="SALIDA">Salidas</SelectItem>
              </SelectContent>
            </Select>

            <Select value={metodo} onValueChange={(v) => resetPage(setMetodo)(v as MetodoFiltro)}>
              <SelectTrigger><SelectValue placeholder="Método" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="TODOS">Todos los métodos</SelectItem>
                <SelectItem value="QR_SCAN">QR</SelectItem>
                <SelectItem value="BUSQUEDA_MANUAL">Manual</SelectItem>
              </SelectContent>
            </Select>

            {/* Rango de fechas: ocupa 2 columnas para que ambos inputs respiren */}
            <div className="flex items-center gap-2 md:col-span-2 lg:col-span-2">
              <Input
                type="date"
                value={desde}
                onChange={(e) => resetPage(setDesde)(e.target.value)}
                aria-label="Desde"
                className="flex-1 min-w-0"
              />
              <span className="shrink-0 text-muted-foreground text-sm">—</span>
              <Input
                type="date"
                value={hasta}
                onChange={(e) => resetPage(setHasta)(e.target.value)}
                aria-label="Hasta"
                className="flex-1 min-w-0"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Tabla de historial ──────────────────────────────────────────── */}
      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">Colaborador</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead className="hidden md:table-cell">Equipo</TableHead>
              <TableHead className="hidden sm:table-cell">Método</TableHead>
              <TableHead className="text-right">Fecha y hora</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loadingMovs && !movimientos ? (
              [...Array(8)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={5}><Skeleton className="h-10 w-full" /></TableCell>
                </TableRow>
              ))
            ) : !movimientos?.items.length ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <DoorClosed className="h-8 w-8 opacity-40" />
                    <p className="text-sm">No hay movimientos que coincidan con los filtros</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              movimientos.items.map((m) => (
                <TableRow key={m.id_movimiento}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium text-sm">{m.empleado.nombres}</span>
                      <span className="text-xs text-muted-foreground tabular">CC {m.empleado.documento}</span>
                    </div>
                  </TableCell>
                  <TableCell><TipoBadge tipo={m.tipo_movimiento} /></TableCell>
                  <TableCell className="hidden md:table-cell"><EquipoCell mov={m} /></TableCell>
                  <TableCell className="hidden sm:table-cell"><MetodoBadge metodo={m.metodo_validacion} /></TableCell>
                  <TableCell className="text-right tabular text-sm whitespace-nowrap">
                    {new Date(m.fecha_hora).toLocaleString('es-CO', {
                      day: '2-digit', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit',
                    })}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* ── Paginación ────────────────────────────────────────────────── */}
        {!!movimientos?.total && (
          <div className="flex items-center justify-between border-t px-4 py-3">
            <p className="text-xs text-muted-foreground">
              {movimientos.total} movimiento{movimientos.total === 1 ? '' : 's'} · Página {page} de {totalPaginas}
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage((p) => Math.min(totalPaginas, p + 1))}
                disabled={page >= totalPaginas}
              >
                Siguiente
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
